import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import { useEditable } from '../context/EditableContext';
import InlineEditor from './InlineEditor';

const HeroSection = () => {
  const { isEditMode, siteConfig, setSiteConfig, languageButtons, setLanguageButtons } = useEditable();
  const [editingHero, setEditingHero] = useState(false);
  const [editingButton, setEditingButton] = useState(null);
  const [editPosition, setEditPosition] = useState({ top: 0, left: 0 });

  const handleHeroEdit = (e) => {
    if (!isEditMode) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setEditPosition({ top: rect.top + 50, left: rect.left + rect.width / 2 - 160 });
    setEditingHero(true);
  };

  const handleButtonEdit = (e, button) => {
    if (!isEditMode) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setEditPosition({ top: rect.bottom + 10, left: rect.left });
    setEditingButton(button);
  };

  const handleHeroSave = (updatedData) => {
    setSiteConfig(prev => ({ ...prev, ...updatedData }));
    setEditingHero(false);
  };

  const handleButtonSave = (updatedData) => {
    setLanguageButtons(prev => 
      prev.map(btn => btn.id === editingButton.id ? { ...btn, ...updatedData } : btn)
    );
    setEditingButton(null);
  };

  return (
    <section className="relative min-h-[50vh] sm:min-h-[60vh] flex flex-col">
      {/* Hero Background */}
      <div 
        className={`relative h-[35vh] sm:h-[45vh] flex items-center justify-center bg-cover bg-center bg-no-repeat ${isEditMode ? 'cursor-pointer ring-2 ring-transparent hover:ring-green-400' : ''}`}
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(${siteConfig.heroBackgroundImage})`
        }}
        onClick={handleHeroEdit}
      >
        <div className="text-center px-4 relative">
          {isEditMode && (
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-green-500 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
              <Pencil size={12} /> Click to edit
            </div>
          )}
          <h1 className="text-2xl sm:text-4xl md:text-6xl lg:text-7xl font-light text-white tracking-[0.1em] sm:tracking-[0.15em] mb-3 sm:mb-4">
            {siteConfig.heroTitle}
          </h1>
          <div className="w-48 sm:w-64 md:w-96 h-1 bg-[#5f7c8a] mx-auto" />
        </div>
      </div>

      {/* Language Buttons Section */}
      <div className="bg-white py-8 sm:py-12 px-4">
        <div className="max-w-xl mx-auto space-y-3 sm:space-y-4">
          {languageButtons.map((button) => (
            <div key={button.id} className="relative group">
              {isEditMode && (
                <button
                  onClick={(e) => handleButtonEdit(e, button)}
                  className="absolute -right-12 top-1/2 -translate-y-1/2 bg-green-500 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10"
                >
                  <Pencil size={14} />
                </button>
              )}
              <Link
                to={button.href}
                onClick={(e) => isEditMode && e.preventDefault()}
                className={`block w-full py-3 sm:py-3.5 text-center text-white text-sm sm:text-base font-medium tracking-wider rounded-sm transition-all duration-300 hover:opacity-90 hover:shadow-lg hover:scale-[1.02] ${isEditMode ? 'ring-2 ring-transparent hover:ring-green-400' : ''}`}
                style={{ backgroundColor: button.color }}
              >
                {button.label}
              </Link>
            </div>
          ))}
        </div>
        
        {/* Decorative Line */}
        <div className="max-w-3xl mx-auto mt-8 sm:mt-12">
          <div className="h-0.5 bg-gradient-to-r from-transparent via-[#5f7c8a] to-transparent" />
        </div>
      </div>

      {/* Inline Editors */}
      {editingHero && (
        <InlineEditor
          type="siteConfig"
          data={siteConfig}
          onSave={handleHeroSave}
          onClose={() => setEditingHero(false)}
          position={editPosition}
        />
      )}

      {editingButton && (
        <InlineEditor
          type="button"
          data={editingButton}
          onSave={handleButtonSave}
          onClose={() => setEditingButton(null)}
          position={editPosition}
        />
      )}
    </section>
  );
};

export default HeroSection;
